"use client"

import { useId } from "react"
import clsx from "clsx"
import { FormattedMessage } from "react-intl"

import { Box, H3, Loading, P, Toggle } from "@bullseye/components"

import { SettingElement, SingleSetting } from "../SettingElement"

type ChannelSettingsProps = {
  title: JSX.Element | string
  all?: SingleSetting
  allOn: boolean
  setAllOn: (v: boolean) => void
  isAllLoading: boolean
  elements: SingleSetting[]
  toggleElement: (elementIdx: number, v: boolean) => void
  elementsLoading: boolean
}

const ElementToggle = ({
  element,
  idx,
  allOn,
  elementsLoading,
  toggleElement,
  isLast,
}: {
  element: SingleSetting
  idx: number
  allOn: boolean
  elementsLoading: boolean
  toggleElement: (idx: number, v: boolean) => void
  isLast: boolean
}) => {
  const labelId = useId()
  return (
    <SettingElement
      disabled={!allOn || elementsLoading}
      settingLabel={element}
      labelId={labelId}
      className={clsx(
        !isLast
          ? "border-b border-zinc-300"
          : "pb-0 md:border-b md:border-zinc-300 md:pb-4",
      )}
    >
      {elementsLoading ? (
        <Loading size="small" />
      ) : (
        <Toggle
          value={!!element.enabled}
          onChange={(v) => {
            if (!allOn || elementsLoading) return
            toggleElement(idx, v)
          }}
          aria-labelledby={labelId}
        />
      )}
    </SettingElement>
  )
}

export const ChannelSettings = ({
  title,
  all,
  elements,
  allOn,
  setAllOn,
  toggleElement,
  elementsLoading,
  isAllLoading,
}: ChannelSettingsProps) => {
  const allLabelId = useId()

  return (
    <Box className="w-full">
      <H3>{title}</H3>
      {all && (
        <SettingElement
          settingLabel={all}
          className="border-b border-zinc-300 py-4"
          disabled={isAllLoading}
          labelId={allLabelId}
        >
          {isAllLoading ? (
            <Loading size="small" />
          ) : (
            <>
              {allOn ? (
                <P className="text-violet-600">
                  <FormattedMessage id="settings.notifications.all-on" />
                </P>
              ) : (
                <P className="text-gray-600">
                  <FormattedMessage id="settings.notifications.all-off" />
                </P>
              )}
              <Toggle
                value={allOn}
                onChange={(v) => {
                  if (isAllLoading) return
                  setAllOn(v)
                }}
                aria-labelledby={allLabelId}
              />
            </>
          )}
        </SettingElement>
      )}
      {elements.map((element, idx) => (
        <ElementToggle
          key={idx}
          element={element}
          idx={idx}
          allOn={allOn}
          elementsLoading={elementsLoading}
          toggleElement={toggleElement}
          isLast={idx === elements.length - 1}
        />
      ))}
    </Box>
  )
}
